/**
 * 新規登録者用の管理コンポーネント
 * @returns
 */
import React, { useState, useEffect } from 'react';
import { UserInfo } from '../../types/userInfo';
import ReferralWelcomeForInvitee from '../userReferral/ReferralWelcomeForInvitee';
import OnboardingManager from './onboarding/OnboardingManager';
import Modal from '../../components/Modal';
import { Button } from '../../components/Button';
import { useTranslation } from 'react-i18next';
import RegistrationFeedbackModal from './RegistrationFeedbackModal';

interface FirstTimeExperienceManagerProps {
    userInfo: UserInfo | null;
    children: React.ReactNode;
}

const FirstTimeExperienceManager: React.FC<FirstTimeExperienceManagerProps> = ({
    userInfo,
    children,
}) => {
    const { t } = useTranslation('referral');
    const [showReferralModal, setShowReferralModal] = useState<boolean>(false);
    const [referralModalCompleted, setReferralModalCompleted] = useState<boolean>(false);
    const [showOnboarding, setShowOnboarding] = useState<boolean>(false);
    const [showRegistrationFeedback, setShowRegistrationFeedback] = useState<boolean>(false);

    // 新規ユーザーかどうかの判定
    useEffect(() => {
        const onboardingCompleted = sessionStorage.getItem('onboarding_completed') === 'true';

        const isNewUser =
            (userInfo?.isNewlyRegistered || sessionStorage.getItem('isNewUser') === 'true') &&
            !onboardingCompleted;

        if (isNewUser) {
            setShowOnboarding(true);
        } else {
            setShowOnboarding(false);
        }
    }, [userInfo]);

    useEffect(() => {
        const handleInviteeWelcome = () => {
            setShowReferralModal(true);
        };

        const handleOnboardingCompleted = () => {
            setShowOnboarding(false);
            const alreadyCompleted =
                sessionStorage.getItem('registration_feedback_completed') === 'true';
            if (!alreadyCompleted) {
                setShowRegistrationFeedback(true);
            }
        };

        window.addEventListener('invitee-welcome-show', handleInviteeWelcome);
        window.addEventListener('onboarding-completed', handleOnboardingCompleted);

        return () => {
            window.removeEventListener('invitee-welcome-show', handleInviteeWelcome);
            window.removeEventListener('onboarding-completed', handleOnboardingCompleted);
        };
    }, []);

    // 被紹介者向けウェルカムモーダル完了
    const completeReferralModal = () => {
        setShowReferralModal(false);
        setReferralModalCompleted(true);
    };

    const completeRegistrationFeedback = () => {
        setShowRegistrationFeedback(false);
        sessionStorage.setItem('registration_feedback_completed', 'true');
    };

    // 被紹介者向けウェルカムモーダルの表示
    const renderReferralModal = () => {
        if (!showReferralModal) return null;

        return (
            <Modal
                isOpen={showReferralModal}
                onClose={completeReferralModal}
                contentClassName="w-full max-w-xl p-10"
            >
                <ReferralWelcomeForInvitee />
                <div className="mt-12 flex justify-center">
                    <Button
                        variant="secondary"
                        className="px-6 w-40"
                        onClick={completeReferralModal}
                    >
                        {t('actions.start')}
                    </Button>
                </div>
            </Modal>
        );
    };

    return (
        <>
            {children}
            {renderReferralModal()}

            {/* 新規ユーザーの場合のみオンボーディングを表示 */}
            {showOnboarding && (
                <>
                    {/* 紹介モーダル完了後、または紹介モーダルが表示されない場合にオンボーディングを表示 */}
                    {(referralModalCompleted || !showReferralModal) && (
                        <OnboardingManager>
                            <div />
                        </OnboardingManager>
                    )}
                </>
            )}

            {/* フィードバックモーダルを表示 */}
            <RegistrationFeedbackModal
                isOpen={showRegistrationFeedback}
                onClose={completeRegistrationFeedback}
            />
        </>
    );
};

export default FirstTimeExperienceManager;
